import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Library, Sparkles } from 'lucide-react'
import { useMinCopy } from '@/i18n/useUiLocale'
import { ReadingHubSection } from '@/components/reading/ReadingHubSection'
import { ArticleLibrarySection } from '@/components/reading/ArticleLibrarySection'
import { readingApi } from '@/api/reading'
import { useDebounce } from '@/hooks/useDebounce'
import type { ArticleReadStatus } from '@/api/articles'
import type { CefrLevel } from '@/types/common'
import { cn } from '@/components/ui/cn'

type ReadingSection = 'hub' | 'library'

export function ArticlesPage() {
  const { reading } = useMinCopy()
  const sectionTabs = useMemo(
    () =>
      [
        { id: 'hub' as const, label: reading.tabForYou, icon: Sparkles },
        { id: 'library' as const, label: reading.tabLibrary, icon: Library },
      ] as const,
    [reading.tabForYou, reading.tabLibrary],
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSection = searchParams.get('section') === 'library' ? 'library' : 'hub'
  const [section, setSection] = useState<ReadingSection>(initialSection)
  const [search, setSearch] = useState('')
  const [cefrLevel, setCefrLevel] = useState<CefrLevel | ''>('')
  const [activeTag, setActiveTag] = useState('')
  const [readStatus, setReadStatus] = useState<ArticleReadStatus>('ALL')
  const [page, setPage] = useState(0)

  const debouncedSearch = useDebounce(search, 300)
  const hasActiveFilters = Boolean(debouncedSearch || cefrLevel || activeTag || readStatus !== 'ALL')

  useEffect(() => {
    if (searchParams.get('section') === 'library') {
      setSection('library')
    }
  }, [searchParams])

  useEffect(() => {
    if (hasActiveFilters) {
      setSection('library')
    }
  }, [hasActiveFilters])

  const handleSectionChange = (nextSection: ReadingSection) => {
    setSection(nextSection)
    const nextParams = new URLSearchParams(searchParams)
    if (nextSection === 'library') {
      nextParams.set('section', 'library')
    } else {
      nextParams.delete('section')
    }
    setSearchParams(nextParams, { replace: true })
  }

  const recommendationQuery = useQuery({
    queryKey: ['readingRecommended', 'default'],
    queryFn: () => readingApi.getRecommended('default'),
    enabled: section === 'hub' && !hasActiveFilters,
    retry: false,
  })

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <div
          className="inline-flex rounded-xl border border-border/60 bg-muted/20 p-1"
          data-testid="reading-section-tabs"
          role="tablist"
        >
          {sectionTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={section === id}
              data-testid={`reading-section-${id}`}
              onClick={() => handleSectionChange(id)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                section === id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {section === 'hub' ? (
        <ReadingHubSection
          recommendation={recommendationQuery.data}
          isLoading={recommendationQuery.isLoading}
          isError={recommendationQuery.isError}
          onBrowseLibrary={() => handleSectionChange('library')}
        />
      ) : (
        <ArticleLibrarySection
          search={debouncedSearch}
          cefrLevel={cefrLevel}
          activeTag={activeTag}
          readStatus={readStatus}
          page={page}
          onSearchChange={setSearch}
          onCefrChange={setCefrLevel}
          onTagChange={setActiveTag}
          onReadStatusChange={setReadStatus}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
